import type { RootState } from 'client/js/redux';
import { createSelector } from 'reselect';
import { SummaryProductContextService } from '../../../../../../shared/SummaryProductContext.service';
import { SUMMARY_TILE } from '../../../../../../shared/constants/analytics/contexts';
import { LAST_ATTRIBUTED_TOUCH_DATA_TILDE_CAMPAIGN_NAME } from '../../../../../../shared/constants/cenariusDimensions';
import { NEW_SUMMARY_NAMESPACE } from '../../../../../../shared/constants/shareableReports';
import { ALL_DATA_VALUE, QUICK_LINKS_VALUE, UNIVERSAL_EMAIL_VALUE } from '../../../../../../shared/constants/summary';
import type { ProductType } from '../../../../../../shared/types/summary.types';
import InFilter from '../../../../common/support/utils/druid/filters/InFilter';
import type { ShareableData, SingleNamespaceData } from '../../../../common/types/shareableTypes';
import { isTouchPointAnalyticsAndDashboardReportingEnabled } from '../../../selectors/ProductSelectors';
import { getUnarchivedLinks as getUnarchivedQuickLinks } from '../../../selectors/QuickLinksSelectors';
import { handleGetDateRangeInfo } from '../../../support/dateUtils';
import type { DateRangeInfo } from '../../../types/dateRangeTypes';
import type { Filter, SourceQuery } from '../../../types/druidTypes';
import EmailsTimeSeriesService from '../../ScreenEmail/Analytics/EmailsTimeSeries/EmailsTimeSeries.service';
import {
  INIT_DEFAULT_RANGE,
  PRODUCT_COMPARE_BY_MAPPING,
  PRODUCT_DATA_SOURCES_REPORT_MAPPING,
  PRODUCT_DATA_SOURCES_TRENDS_MAPPING,
  PRODUCT_EVENT_LIST_MAPPING,
  PRODUCT_HAS_ORGANIC_TOGGLE_MAPPING,
  PRODUCT_HAS_WEB_ONLY_TOGGLE_MAPPING,
  PRODUCT_REPORT_DEFAULT_DIMENSIONS_MAPPING,
  PRODUCT_REPORT_DEFAULT_FILTER_MAPPING,
  PRODUCT_REQUIRED_FILTER_MAPPING,
  PRODUCT_SOURCE_QUERY_MAPPING,
  PRODUCT_TILES_MAPPING,
  PRODUCT_TRENDS_DEFAULT_DIMENSIONS_MAPPING,
  PRODUCT_TRENDS_DEFAULT_FILTER_MAPPING,
  QUICK_LINKS_FILTER_DIMENSION,
  TOUCH_POINT_ANALYTICS_COMPARE_BY_DIMENSIONS,
} from '../newSummaryConstants';
import type { SummaryTileConfig } from '../newSummaryTypes';

const FALLBACK_PRODUCT = ALL_DATA_VALUE;

const getShareableState = (state: RootState) => state.shareable;

const getShareableData: (state: RootState) => ShareableData = createSelector(
  getShareableState,
  (shareableState) => shareableState.data
);

const getSummaryState: (state: RootState) => SingleNamespaceData = createSelector(
  getShareableData,
  (shareableData) => shareableData[NEW_SUMMARY_NAMESPACE] || {}
);

export const getDateRangeInfo: (state: RootState) => DateRangeInfo = createSelector(getSummaryState, (summaryState) => {
  const { dateRange, defaultRange } = summaryState;
  const dateRangeInfo: DateRangeInfo = {
    dateRange,
    defaultRange,
  };
  return handleGetDateRangeInfo(dateRangeInfo, INIT_DEFAULT_RANGE);
});

export const getSelectedProduct: (state: RootState) => ProductType = createSelector(
  getSummaryState,
  (summaryState) => summaryState.selectedProduct || FALLBACK_PRODUCT
);

export const getRequiredFilter: (state: RootState) => Filter = createSelector(
  getSelectedProduct,
  getUnarchivedQuickLinks,
  (selectedProduct, links) => {
    if (selectedProduct === QUICK_LINKS_VALUE) {
      const quickLinksRequiredFilter = new InFilter(
        QUICK_LINKS_FILTER_DIMENSION,
        links.map((link) => link.id)
      );
      return links.length ? quickLinksRequiredFilter : {};
    }

    return PRODUCT_REQUIRED_FILTER_MAPPING[selectedProduct];
  }
);

export const selectHasWebOnlyToggle: (state: RootState) => boolean = createSelector(
  getSelectedProduct,
  (selectedProduct) => PRODUCT_HAS_WEB_ONLY_TOGGLE_MAPPING[selectedProduct]
);

export const selectHasOrganicToggle: (state: RootState) => boolean = createSelector(
  getSelectedProduct,
  (selectedProduct) => {
    return PRODUCT_HAS_ORGANIC_TOGGLE_MAPPING[selectedProduct];
  }
);
export const getSourceQuery: (state: RootState) => SourceQuery = createSelector(
  getSelectedProduct,
  (selectedProduct) => PRODUCT_SOURCE_QUERY_MAPPING[selectedProduct]
);

export const getTileConfig: (state: RootState) => SummaryTileConfig = createSelector(
  getSummaryState,
  getSelectedProduct,
  (summaryState, selectedProduct) => {
    const context = SummaryProductContextService.encodeContext(SUMMARY_TILE, selectedProduct);

    return summaryState[context] || PRODUCT_TILES_MAPPING[selectedProduct];
  }
);

export const selectCompareBy = createSelector(
  getSelectedProduct,
  isTouchPointAnalyticsAndDashboardReportingEnabled,
  (selectedProduct, isTouchPointAnalyticsEnabled) => {
    const baseDimensions = PRODUCT_COMPARE_BY_MAPPING[selectedProduct];

    // If the app has touch point analytics, simplify the options
    if (isTouchPointAnalyticsEnabled) {
      // Filter to only include dimensions that exist in the base dimensions
      return baseDimensions.filter((dim) => TOUCH_POINT_ANALYTICS_COMPARE_BY_DIMENSIONS.includes(dim));
    }

    return baseDimensions;
  }
);

export const selectEventList = createSelector(
  getSelectedProduct,
  isTouchPointAnalyticsAndDashboardReportingEnabled,
  (selectedProduct, isTouchPointAnalyticsEnabled) => {
    const events = PRODUCT_EVENT_LIST_MAPPING[selectedProduct];

    // If the app has touch point analytics, only show eo_click event
    if (isTouchPointAnalyticsEnabled) {
      return events.filter((event) => event === 'CLICK');
    }

    return events;
  }
);

export const selectReportsDataSources = createSelector(
  getSelectedProduct,
  isTouchPointAnalyticsAndDashboardReportingEnabled,
  (selectedProduct, isTouchPointAnalyticsEnabled) => {
    // If app has touch point analytics, only show eo_click event
    if (isTouchPointAnalyticsEnabled) {
      return ['eo_click'];
    }

    return PRODUCT_DATA_SOURCES_REPORT_MAPPING[selectedProduct];
  }
);

export const selectTrendsDataSources = createSelector(
  getSelectedProduct,
  (state: RootState) => state.emailOnboarding?.upsell?.hasRevenueEvents,
  isTouchPointAnalyticsAndDashboardReportingEnabled,
  (selectedProduct, hasRevenueEvents, isTouchPointAnalyticsEnabled) => {
    // If the app has touch point analytics, only show eo_click event
    if (isTouchPointAnalyticsEnabled) {
      return ['eo_click'];
    }

    if (selectedProduct === UNIVERSAL_EMAIL_VALUE) {
      return EmailsTimeSeriesService.getDefaultSummaryDataSources(hasRevenueEvents);
    }

    return PRODUCT_DATA_SOURCES_TRENDS_MAPPING[selectedProduct];
  }
);

export const selectDefaultReportDimensions = createSelector(
  getSelectedProduct,
  isTouchPointAnalyticsAndDashboardReportingEnabled,
  (selectedProduct, isTouchPointAnalyticsEnabled) => {
    // If the app has touch point analytics, default to campaign name
    if (isTouchPointAnalyticsEnabled) {
      return [LAST_ATTRIBUTED_TOUCH_DATA_TILDE_CAMPAIGN_NAME];
    }

    return PRODUCT_REPORT_DEFAULT_DIMENSIONS_MAPPING[selectedProduct];
  }
);

export const selectDefaultTrendsDimensions = createSelector(
  getSelectedProduct,
  isTouchPointAnalyticsAndDashboardReportingEnabled,
  (selectedProduct, isTouchPointAnalyticsEnabled) => {
    // If the app has touch point analytics, default to campaign name
    if (isTouchPointAnalyticsEnabled) {
      return [LAST_ATTRIBUTED_TOUCH_DATA_TILDE_CAMPAIGN_NAME];
    }

    return PRODUCT_TRENDS_DEFAULT_DIMENSIONS_MAPPING[selectedProduct];
  }
);

export const selectReportDefaultFilters = createSelector(getSelectedProduct, (selectedProduct) => {
  return PRODUCT_REPORT_DEFAULT_FILTER_MAPPING[selectedProduct];
});

export const selectTrendsDefaultFilters = createSelector(getSelectedProduct, (selectedProduct) => {
  return PRODUCT_TRENDS_DEFAULT_FILTER_MAPPING[selectedProduct];
});
